import React, { createContext, useContext, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { CurrentSubView, ListStatus, RetorikEvent, UIPart } from '../../models/enums'
import type { CustomMenu, GDPR } from '../../models/types'
import { RetorikActivity, DetailedAttachmentAction } from '../../models/activityTypes'

interface UtilsState {
  fullScreenImage: string | undefined
  lastListActivity: string | undefined
  listStatus: ListStatus
  currentSubView: number
  currentCustomView: CustomMenu | undefined
  retorikEvent: RetorikEvent | undefined
  displayAgent: boolean
  activityToSend: RetorikActivity | undefined
  retorikNewsEnded: boolean
  userIsTyping: boolean
  userIsSwiping: boolean
  askedForKioskParametersOpening: number
  detailedAttachmentTriggers: Array<DetailedAttachmentAction>
  modalFullscreenContent: JSX.Element | null
  modalFullscreenContentNoDisplayId: string | undefined
  printingCallback: ((url: string) => void) | null
  GDPRDisplay: boolean
  GDPRWindowData: GDPR | undefined
  GDPRUserConsent: boolean
  unlockClickedButton: boolean
  dashboardOpened: boolean
  clickedOnUI: UIPart
  closeDetailedPOI: boolean
  closeList: number
  actions: {
    setFullScreenImage: (value: string | undefined) => void
    setLastListActivity: (value: string | undefined) => void
    getListStatus: () => ListStatus
    setListStatus: (value: ListStatus) => void
    setCloseList: (value: number) => void
    setCurrentSubView: (value: number) => void
    setCurrentCustomView: (value: CustomMenu) => void
    setRetorikEvent: (value: RetorikEvent) => void
    showAgent: () => void
    hideAgent: () => void
    sendActivity: (value: RetorikActivity | undefined) => void
    setRetorikNewsEnded: (value: boolean) => void
    setUserTyping: (value: boolean) => void
    setUserSwiping: () => void
    askForKioskParametersOpening: () => void
    addDetailedAttachmentTrigger: (value: DetailedAttachmentAction) => void
    setModalFullscreenContent: (value: JSX.Element | null, fromActivity?: string) => void
    setPrintingCallback: (value: ((url: string) => void) | null) => void
    setGDPRDisplay: (value: boolean) => void
    setGDPRWindowData: (value: GDPR | undefined) => void
    setGDPRUserConsent: (value: boolean) => void
    lockClickedButton: () => void
    unlockClickedButton: () => void
    setDashboardOpened: (value: boolean) => void
    toggleDashboard: (doNotCloseViews?: boolean) => void
    setClickedOnUI: (value: UIPart) => void
    setCloseDetailedPOI: (value: boolean) => void
    resetUtilsStore: () => void
  }
}

let timer: NodeJS.Timer | null = null

const initialState = {
  fullScreenImage: undefined,
  lastListActivity: undefined,
  listStatus: ListStatus.available,
  currentSubView: CurrentSubView.none,
  currentCustomView: undefined,
  retorikEvent: undefined,
  displayAgent: true,
  activityToSend: undefined,
  retorikNewsEnded: false,
  userIsTyping: false,
  userIsSwiping: false,
  askedForKioskParametersOpening: 0,
  detailedAttachmentTriggers: [
    {
      type: 'event',
      name: 'hospitality.ShowPointOfInterest',
      linkedComponent: null
    },
    {
      type: 'event',
      name: 'hospitality.ShowContentItem',
      linkedComponent: null
    }
  ],
  modalFullscreenContent: null,
  modalFullscreenContentNoDisplayId: undefined,
  printingCallback: null,
  GDPRDisplay: true,
  GDPRWindowData: undefined,
  GDPRUserConsent: false,
  unlockClickedButton: false,
  dashboardOpened: false,
  clickedOnUI: UIPart.none,
  closeDetailedPOI: false,
  closeList: 0
}

const createUtilsStore = () =>
  createStore<UtilsState>((set, get) => {
    return {
      ...initialState,
      actions: {
        setFullScreenImage: (value: string | undefined) => {
          set({
            fullScreenImage: value
          })
        },
        setLastListActivity: (value: string | undefined) => {
          set({
            lastListActivity: value
          })
        },
        setListStatus: (value: ListStatus) => {
          set({
            listStatus: value
          })
        },
        getListStatus: () => {
          return get().listStatus
        },
        setCloseList: (value: number) => {
          set({
            closeList: value
          })
        },
        setCurrentSubView: (value: number) => {
          set({
            currentSubView: value
          })
        },
        setCurrentCustomView: (value: CustomMenu) => {
          set({
            currentCustomView: value
          })
        },
        setRetorikEvent: (value: RetorikEvent) => {
          set({
            retorikEvent: value
          })
        },
        showAgent: () => {
          set({
            displayAgent: true
          })
        },
        hideAgent: () => {
          set({
            displayAgent: false
          })
        },
        sendActivity: (value: RetorikActivity | undefined) => {
          set({
            activityToSend: value
          })
        },
        setRetorikNewsEnded: (value: boolean) => {
          set({
            retorikNewsEnded: value
          })
        },
        setUserTyping: (value: boolean) => {
          set({
            userIsTyping: value
          })
        },
        setUserSwiping: () => {
          timer && clearTimeout(timer)
          set({
            userIsSwiping: true
          })
          timer = setTimeout(() => {
            set({
              userIsSwiping: false
            })
          }, 2500)
        },
        askForKioskParametersOpening: () => {
          set((state) => ({
            askedForKioskParametersOpening: state.askedForKioskParametersOpening + 1
          }))
        },
        addDetailedAttachmentTrigger: (value: DetailedAttachmentAction) => {
          set((state) => ({
            detailedAttachmentTriggers: [...state.detailedAttachmentTriggers, value]
          }))
        },
        setModalFullscreenContent: (value: JSX.Element | null, fromActivity?: string) => {
          set((state) => {
            if (!fromActivity) {
              return {
                modalFullscreenContent: value
              }
            } else if (fromActivity !== state.modalFullscreenContentNoDisplayId) {
              // Set fromActivity data to check next time if we have to prevent unwanted re-rendering
              return {
                modalFullscreenContent: value,
                modalFullscreenContentNoDisplayId: fromActivity
              }
            }

            return state
          })
        },
        setPrintingCallback: (value: ((url: string) => void) | null) => {
          set({
            printingCallback: value
          })
        },
        setGDPRDisplay: (value: boolean) => {
          set({
            GDPRDisplay: value
          })
        },
        setGDPRWindowData: (value: GDPR | undefined) => {
          set(() => ({
            GDPRWindowData: value
          }))
        },
        setGDPRUserConsent: (value: boolean) => {
          set({
            GDPRUserConsent: value
          })
        },
        lockClickedButton: () => {
          set({
            unlockClickedButton: false
          })
        },
        unlockClickedButton: () => {
          set({
            unlockClickedButton: true
          })
        },
        setDashboardOpened: (value: boolean) => {
          set({
            dashboardOpened: value
          })
        },
        toggleDashboard: (doNotCloseViews?: boolean) => {
          if (doNotCloseViews) {
            set((state) => ({
              dashboardOpened: !state.dashboardOpened
            }))
          } else {
            set((state) => ({
              currentSubView: CurrentSubView.none,
              dashboardOpened: !state.dashboardOpened
            }))
          }
        },
        setClickedOnUI: (value: UIPart) => {
          set({
            clickedOnUI: value
          })
          setTimeout(() => {
            set({
              clickedOnUI: UIPart.none
            })
          }, 50)
        },
        setCloseDetailedPOI: (value: boolean) => {
          set({
            closeDetailedPOI: value
          })
        },
        resetUtilsStore: () => {
          set({
            ...initialState
          })
        }
      }
    }
  })

const UtilsContext = createContext<StoreApi<UtilsState> | null>(null)

const useUtilsStore = <T,>(selector: (state: UtilsState) => T): T => {
  const store = useContext(UtilsContext)
  if (!store) throw new Error('Retorik Framework > useUtilsStore must be used within UtilsProvider')
  return useStore(store, selector)
}

const UtilsProvider = ({ children }) => {
  const [store] = useState(() => createUtilsStore())

  return <UtilsContext.Provider value={store}>{children}</UtilsContext.Provider>
}

export default UtilsProvider
export { useUtilsStore }
export type { UtilsState }
